import { motion } from "framer-motion";
import {
  BarChart3,
  BellRing,
  ChevronRight,
  CreditCard,
  FileDown,
  MonitorPlay,
  QrCode,
  ShoppingBag,
  Smartphone,
  TrendingUp,
  Tv,
  UtensilsCrossed,
  Wifi,
  Zap,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useModal } from "@/context/ModalContext";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { MARKET } from "@/market";

// Компонент мокапа владельца
function OwnerMockup() {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="rounded-2xl bg-dark-card border border-dark-border shadow-hero p-5 hover:border-primary/30 transition-all duration-300"
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-white/40">
          {t("dashboardPreview.owner.mockup.title")}
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
          <div className="text-[10px] text-green-400 font-semibold">{t("dashboardPreview.owner.mockup.live")}</div>
        </div>
      </div>

      <div className="text-white text-[36px] font-bold mt-2 tracking-[-0.02em]">
        {MARKET.mockMoney(12480000)}
        <span className="text-[14px] text-white/40 font-normal ml-1">{t("dashboardPreview.owner.mockup.revenue")}</span>
      </div>

      <div className="flex items-center gap-1 text-[12px] text-green-400 font-semibold mt-1">
        <TrendingUp className="w-3 h-3" />↑ 18.3% {t("dashboardPreview.owner.mockup.week")}
      </div>

      {/* График */}
      <svg viewBox="0 0 400 100" className="w-full mt-4 h-24">
        <defs>
          <linearGradient id="og1" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2563EB" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d="M0,80 L40,70 L80,75 L120,55 L160,60 L200,30 L240,45 L280,20 L320,35 L360,15 L400,25 L400,100 L0,100 Z"
          fill="url(#og1)"
        />
        <path
          d="M0,80 L40,70 L80,75 L120,55 L160,60 L200,30 L240,45 L280,20 L320,35 L360,15 L400,25"
          fill="none"
          stroke="#60A5FA"
          strokeWidth="2"
        />
      </svg>

      <div className="grid grid-cols-3 gap-3 mt-4">
        {[
          ["chilanzar", "4.2M", "+12%"],
          ["yunusabad", "5.1M", "+22%"],
          ["samarkand", "3.2M", "+8%"],
        ].map(([key, value, percent]) => (
          <div
            key={key}
            className="rounded-lg bg-[#0F172A] border border-dark-border p-3 hover:border-primary/30 transition-all"
          >
            <div className="text-[10px] text-white/40">{t(`dashboardPreview.owner.mockup.branches.${key}`)}</div>
            <div className="text-white text-[16px] font-bold mt-1">{value}</div>
            <div className="text-[10px] text-green-400 mt-0.5">{percent}</div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

/**
 * QR-menu preview. Slides are rendered from real menu data so the section never
 * ships an empty placeholder; drop a screenshot path into `src` to use a photo
 * of the actual app instead.
 */
const qrSlides = [{ key: "menu" }, { key: "dish" }, { key: "cart" }] as const

type QrSlideKey = (typeof qrSlides)[number]["key"]

/** Real screenshots go here, keyed by slide, once they exist. */
const qrScreenshots: Partial<Record<QrSlideKey, string>> = {}

function QrSlideBody({ slideKey }: { slideKey: QrSlideKey }) {
  const { t } = useTranslation();
  const m = (key: string) => t(`features.mock.${key}`);

  const dishes = [
    { key: "plov", price: MARKET.mockMoney(35000), out: false },
    { key: "lagman", price: MARKET.mockMoney(32000), out: false },
    { key: "shashlik", price: MARKET.mockMoney(45000), out: true },
  ];

  if (slideKey === "menu") {
    return (
      <div className="flex h-full flex-col gap-2 p-4">
        <p className="text-[10px] tracking-wide text-white/35">
          {m("table")} 4 · {MARKET.mockVenueName}
        </p>
        {dishes.map((d) => (
          <div
            key={d.key}
            className={`rounded-xl border border-dark-border p-2.5 ${d.out ? "bg-dark-bg/50" : "bg-dark-card"}`}
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className={`text-[12px] font-semibold ${d.out ? "text-white/25 line-through" : "text-white"}`}>
                {m(`dishes.${d.key}`)}
              </span>
              <span className={`text-[11px] ${d.out ? "text-white/20" : "text-white/50"}`}>{d.price}</span>
            </div>
            <p className={`mt-1.5 text-[10px] font-semibold ${d.out ? "text-red-400/70" : "text-primary"}`}>
              {d.out ? m("sold_out") : m("add_to_cart")}
            </p>
          </div>
        ))}
      </div>
    );
  }

  if (slideKey === "dish") {
    return (
      <div className="flex h-full flex-col p-4">
        <div className="flex h-24 items-center justify-center rounded-xl bg-primary/10">
          <UtensilsCrossed className="h-8 w-8 text-primary/40" />
        </div>
        <p className="mt-3 text-[15px] font-bold text-white">{m("dishes.plov")}</p>
        <p className="mt-0.5 text-[10px] text-white/35">320 {m("units.g")}</p>
        <p className="mt-2 font-raleway text-[20px] font-bold text-white">
          {MARKET.mockMoney(35000)} <span className="text-[11px] font-normal text-white/40">{m("currency")}</span>
        </p>
        <div className="mt-auto rounded-lg bg-primary py-2 text-center text-[11px] font-bold text-white">
          {m("add_to_cart")}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col p-4">
      <p className="text-[13px] font-bold text-white">{m("your_order")}</p>
      <div className="mt-2.5 space-y-1.5">
        {dishes
          .filter((d) => !d.out)
          .map((d) => (
            <div key={d.key} className="flex justify-between text-[11px]">
              <span className="text-white/55">{m(`dishes.${d.key}`)} ×1</span>
              <span className="text-white/55">{d.price}</span>
            </div>
          ))}
      </div>
      <div className="my-2.5 border-t border-dark-border" />
      <div className="flex justify-between text-[12px] font-bold text-white">
        <span>{m("total")}</span>
        <span>{MARKET.mockMoney(67000)}</span>
      </div>
      <div className="mt-auto space-y-1.5">
        <div className="rounded-lg bg-primary py-2 text-center text-[11px] font-bold text-white">
          {m("pay_click")}
        </div>
        <div className="rounded-lg border border-dark-border py-2 text-center text-[11px] text-white/45">
          {m("pay_payme")}
        </div>
      </div>
    </div>
  );
}

function QrSwiper() {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="mx-auto w-[260px] sm:w-[280px]"
    >
      {/* Phone frame */}
      <div className="rounded-[36px] bg-dark-bg border-[8px] border-white/10 shadow-2xl overflow-hidden">
        {/* Notch bar */}
        <div className="h-6 bg-gradient-to-r from-primary/20 to-primary/5 flex items-center justify-center">
          <div className="w-16 h-1.5 rounded-full bg-white/10" />
        </div>

        <Swiper
          modules={[Pagination, Autoplay]}
          pagination={{ clickable: true }}
          autoplay={{ delay: 3200, disableOnInteraction: false }}
          loop
          className="qr-swiper"
          style={
            {
              "--swiper-pagination-color": "#00adb5",
              "--swiper-pagination-bullet-inactive-color": "rgba(255,255,255,0.25)",
            } as React.CSSProperties
          }
        >
          {qrSlides.map((slide) => {
            const screenshot = qrScreenshots[slide.key];
            const label = t(`dashboardPreview.qr.slides.${slide.key}`);

            return (
              <SwiperSlide key={slide.key}>
                {screenshot ? (
                  <img
                    src={screenshot}
                    alt={label}
                    className="w-full aspect-[9/17] object-cover"
                    draggable={false}
                  />
                ) : (
                  <div className="w-full aspect-[9/17] bg-dark-bg select-none">
                    <QrSlideBody slideKey={slide.key} />
                  </div>
                )}
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </motion.div>
  );
}

// Компонент мокапа телевизора
function TvMockup() {
  const { t } = useTranslation();

  const items = [
    { key: "lagman", price: MARKET.mockMoney(29000, { short: true }) },
    { key: "plov", price: MARKET.mockMoney(32000, { short: true }) },
    { key: "shashlik", price: MARKET.mockMoney(45000, { short: true }) },
    { key: "manti", price: MARKET.mockMoney(27000, { short: true }) },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 p-3 shadow-2xl"
    >
      <div className="rounded-lg bg-gradient-to-br from-primary/20 via-gray-900 to-emerald-500/20 aspect-video p-6 flex flex-col">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.12em] text-white/50">
              {t("dashboardPreview.monitors.mockup.menu_day")}
            </div>
            <div className="text-white text-[32px] font-bold mt-1 tracking-[-0.02em]">
              {t("dashboardPreview.monitors.mockup.lunch")}
            </div>
          </div>
          <div className="px-3 py-1.5 rounded-full bg-amber-500 text-gray-900 text-[12px] font-bold">
            {t("dashboardPreview.monitors.mockup.action")}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-3 mt-6 flex-1">
          {items.map((item) => (
            <div
              key={item.key}
              className="rounded-lg bg-black/40 backdrop-blur border border-white/10 p-3 flex flex-col justify-end hover:border-primary/50 transition-all"
            >
              <div className="text-white text-[13px] font-semibold">
                {t(`dashboardPreview.monitors.mockup.items.${item.key}`)}
              </div>
              <div className="text-emerald-400 text-[20px] font-bold">{item.price}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-center mt-2 gap-1">
        <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
        <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
        <div className="w-4 h-1.5 rounded-full bg-primary" />
      </div>
    </motion.div>
  );
}

// Основной компонент
const badgeStyles: Record<string, string> = {
  success: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  primary: "bg-primary/10 text-primary border-primary/20",
  purple: "bg-purple-500/10 text-purple-400 border-purple-500/20",
};

const sections = [
  {
    key: "owner",
    bg: "dark",
    badgeColor: "success",
    reverse: false,
    icon: BarChart3,
  },
  {
    key: "qr",
    bg: "light",
    badgeColor: "primary",
    reverse: true,
    icon: QrCode,
  },
  {
    key: "monitors",
    bg: "dark",
    badgeColor: "purple",
    reverse: false,
    icon: MonitorPlay,
  },
];

export function DashboardPreview() {
  const { t } = useTranslation();
  const { open } = useModal();

  const getFeatures = (key: string) => {
    const featuresMap = {
      owner: [
        { icon: BarChart3, textKey: "revenue" },
        { icon: BellRing, textKey: "alert" },
        { icon: FileDown, textKey: "reports" },
      ],
      qr: [
        { icon: Smartphone, textKey: "no_app" },
        { icon: CreditCard, textKey: "payments" },
        { icon: ShoppingBag, textKey: "cart" },
      ],
      monitors: [
        { icon: Tv, textKey: "playlist" },
        { icon: Wifi, textKey: "offline" },
        { icon: Zap, textKey: "update" },
      ],
    };
    return featuresMap[key as keyof typeof featuresMap] || [];
  };

  const getVisual = (key: string) => {
    switch (key) {
      case "owner":
        return <OwnerMockup />;
      case "qr":
        return <QrSwiper />;
      case "monitors":
        return <TvMockup />;
      default:
        return null;
    }
  };

  return (
    <>
      {sections.map((section) => {
        const dark = section.bg === "dark";
        const features = getFeatures(section.key);

        return (
          <section
            key={section.key}
            className={`py-[96px] overflow-hidden ${dark ? "bg-dark-bg" : "bg-gradient-to-b from-white to-gray-50"}`}
          >
            <div className="mx-auto max-w-[1200px] px-6">
              <div
                className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                  section.reverse ? "lg:flex-row-reverse" : ""
                }`}
              >
                {/* Текстовая часть */}
                <motion.div
                  initial={{ opacity: 0, x: section.reverse ? 20 : -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className={section.reverse ? "lg:order-2" : ""}
                >
                  {/* Badge */}
                  <div className="inline-flex items-center gap-2">
                    <div className={`p-1.5 rounded-lg ${badgeStyles[section.badgeColor]} bg-opacity-10`}>
                      <section.icon className="w-3.5 h-3.5" />
                    </div>
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold tracking-wide uppercase border ${badgeStyles[section.badgeColor]}`}
                    >
                      {t(`dashboardPreview.${section.key}.badge`)}
                    </span>
                  </div>

                  {/* Заголовок */}
                  <h3
                    className={`mt-5 text-[32px] sm:text-[40px] font-bold tracking-[-0.02em] leading-[1.2] ${
                      dark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {t(`dashboardPreview.${section.key}.title`)}
                  </h3>

                  {/* Список фич */}
                  <ul className="mt-8 space-y-5">
                    {features.map((feature, idx) => (
                      <motion.li
                        key={idx}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.1 }}
                        className="flex items-start gap-4"
                      >
                        <div
                          className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                            dark ? "bg-primary/15 text-primary" : "bg-primary/10 text-primary"
                          }`}
                        >
                          <feature.icon className="w-4.5 h-4.5" />
                        </div>
                        <div>
                          <p
                            className={`text-[15px] sm:text-[16px] leading-relaxed ${
                              dark ? "text-white/80" : "text-gray-700"
                            }`}
                          >
                            {t(`dashboardPreview.${section.key}.features.${feature.textKey}`)}
                          </p>
                        </div>
                      </motion.li>
                    ))}
                  </ul>

                  {/* CTA ссылка */}
                  <motion.button
                    onClick={open}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                    className={`group inline-flex items-center gap-2 mt-8 text-[14px] font-semibold transition-all ${
                      dark ? "text-primary hover:text-primary-light" : "text-primary hover:text-primary-dark"
                    }`}
                  >
                    {t(`dashboardPreview.${section.key}.cta`)}
                    <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </motion.button>
                </motion.div>

                {/* Визуальная часть */}
                <div className={section.reverse ? "lg:order-1" : ""}>{getVisual(section.key)}</div>
              </div>
            </div>
          </section>
        );
      })}
    </>
  );
}
